import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface HeroSectionProps {
  onRegisterClick: () => void;
}

const HeroSection = ({ onRegisterClick }: HeroSectionProps) => {
  const { t } = useTranslation('choHem');

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage:
            'url(https://public.readdy.ai/ai/img_res/69db261f710b0f77ccf9652ee2c1c293.jpg)',
        }}
      ></div>
      <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/45 to-black/65"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center w-full pt-20 pb-10 md:pt-0 md:pb-0">
        <span className="inline-block px-3 py-1 bg-amber-500/20 border border-amber-400/40 rounded-full text-xs text-amber-300 font-semibold uppercase tracking-widest mb-4 md:mb-6">
          {t('hero.badge')}
        </span>

        <h1 className="text-3xl sm:text-5xl md:text-7xl font-bold mb-3 md:mb-6 leading-tight font-serif">
          <span className="text-white">{t('hero.title.line1')}</span>
          <br />
          <span className="text-amber-400">{t('hero.title.line2')}</span>
        </h1>

        <p className="text-sm sm:text-lg md:text-2xl text-gray-200 mb-3 md:mb-5 max-w-3xl mx-auto leading-relaxed">
          {t('hero.description')}
        </p>

        <p className="text-amber-300 text-base sm:text-xl md:text-3xl font-bold mb-6 md:mb-10">
          {t('hero.price')} <span className="text-white">{t('hero.priceDay')}</span> {t('hero.priceOr')} <span className="text-white">{t('hero.priceMonth')}</span>
        </p>

        <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center items-center">
          <button
            onClick={onRegisterClick}
            className="w-full sm:w-auto px-6 md:px-10 py-3 md:py-4 bg-amber-500 text-black text-sm md:text-lg font-bold rounded-lg hover:bg-amber-400 transition-all transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
          >
            {t('hero.cta.register')}
          </button>
          <a
            href="#kiosk-types"
            className="w-full sm:w-auto text-center px-6 md:px-10 py-3 md:py-4 bg-white/10 backdrop-blur-sm text-white text-sm md:text-lg font-semibold rounded-lg hover:bg-white/20 transition-all border-2 border-white/30 whitespace-nowrap cursor-pointer"
          >
            {t('hero.cta.viewTypes')}
          </a>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-2 md:gap-6 mt-8 md:mt-16 max-w-3xl mx-auto">
          {[
            { icon: 'ri-store-2-line', value: '10 EUR', label: t('hero.stats.price') },
            { icon: 'ri-ruler-line', value: '1.7×2.5m', label: t('hero.stats.size') },
            { icon: 'ri-customer-service-2-line', value: '100%', label: t('hero.stats.support') },
          ].map((s, i) => (
            <div key={i} className="bg-white/10 backdrop-blur-md rounded-lg md:rounded-xl p-2.5 md:p-6 border border-white/20">
              <div className="w-7 h-7 md:w-12 md:h-12 flex items-center justify-center bg-amber-500/20 rounded-full mb-1.5 md:mb-3 mx-auto">
                <i className={`${s.icon} text-sm md:text-2xl text-amber-400`}></i>
              </div>
              <div className="text-sm sm:text-lg md:text-2xl font-bold text-white mb-0.5 md:mb-1">{s.value}</div>
              <p className="text-gray-300 text-[9px] sm:text-xs md:text-sm">{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;